import { TokenGroupProps } from '@amzn/awsui-components-react-v3';
import { OptionDefinition } from '@amzn/awsui-components-react-v3/polaris/internal/components/option/interfaces';
import { FormikContextType } from 'formik';
import * as yup from 'yup';
import { isNaN } from 'lodash';
import { DECIMAL_RADIX } from '../../../../constants';
import i18n from '../../../../i18n';
import { ReceiveException } from '../../../../services/receive-exception/types';
import { OrderTypeOptions, ExceptionTypeOptionsProps } from '../config';

export type FormikState = FormikContextType<ExceptionFormValues>;

export type ExceptionFormState = {
  form: FormikState;
  receiveException?: ReceiveException;
  fetchingException?: boolean;
  fetchError?: string;
  assignedToGroups: OptionDefinition[];
  exceptionTypes: ExceptionTypeOptionsProps[];
  loadingResolverConfig?: boolean;
  fetchConfigError?: string;
};

export const InitialValues = {
  exceptionType: null,
  title: '',
  shipmentReferenceId: '',
  site: '',
  // set default value of order type on form
  orderType: OrderTypeOptions[0],
  orderNumber: '',
  model: '',
  orderLineNumber: null,
  legacyModelId: '',
  holdingLocation: '',
  quantity: '',
  trackingNumber: '',
  serials: [],
  note: '',
  tags: [],
  serial: '',
  entireOrder: true,
  tag: '',
  assignedToId: null,
};

export type ExceptionFormValues = {
  exceptionType: OptionDefinition | null;
  title: string;
  shipmentReferenceId: string;
  site: string;
  orderType: OptionDefinition | null;
  orderNumber: string;
  model: string;
  orderLineNumber: number | null;
  legacyModelId: string;
  holdingLocation: string;
  quantity: number | string;
  trackingNumber: string;
  serials: TokenGroupProps.Item[];
  note: string;
  tags: TokenGroupProps.Item[];
  serial: string;
  entireOrder: boolean;
  tag: string;
  assignedToId: OptionDefinition | null;
};

export const ExceptionFormSchema = yup.object().shape({
  exceptionType: yup
    .mixed()
    .nullable()
    .transform((item) => item && item.value)
    .typeError(i18n.t('Exception type is required'))
    .required(i18n.t('Exception type is required')),
  assignedToId: yup
    .mixed()
    .nullable()
    .transform((item) => item && item.value)
    .typeError(i18n.t('Please select a group to assign the exception'))
    .required(i18n.t('Please select a group to assign the exception')),
  title: yup
    .string()
    .trim()
    .required(i18n.t('A title is required'))
    .max(256, 'Title must be less than 256 characters'),
  site: yup
    .string()
    .trim()
    .matches(/^[a-z0-9]+$/i, {
      excludeEmptyString: true,
      message: i18n.t('Site name can only contain letters and numbers'),
    })
    .required(i18n.t('Site name is required')),
  shipmentReferenceId: yup.string().nullable().trim(),
  orderType: yup
    .mixed()
    .nullable()
    .transform((item) => item && item.value)
    .required(i18n.t('Order type is required')),
  orderNumber: yup.string().trim().required(i18n.t('Order number is required')),
  quantity: yup
    .number()
    .typeError(i18n.t('Quantity must be a positive number'))
    // set default value of quantity to 0 if not entered by user
    .transform((val) => (isNaN(val) ? 0 : val))
    .nullable(true)
    .min(0)
    .max(999999999),
  orderLineNumber: yup
    .number()
    .nullable()
    .when('entireOrder', {
      is: false,
      then: yup
        .number()
        .min(1)
        .max(999999999)
        .typeError(i18n.t('Line Number must be a positive number'))
        .required(i18n.t('Order line number is required, Otherwise select entire Order'))
        .transform((value) => parseInt(value, DECIMAL_RADIX)),
    }),
  trackingNumber: yup.string().trim().nullable(),
  holdingLocation: yup.string().trim().nullable(),
  model: yup
    .string()
    .nullable()
    .when('orderLineNumber', {
      is: (orderLineNumber: string) => orderLineNumber && orderLineNumber.length > 0,
      then: yup.string().required(i18n.t('Please provide the model of the line item')),
    }),
  legacyModelId: yup.string().nullable(),
  notes: yup.string(),
  serials: yup.array().of(yup.mixed().transform((item) => item && item.label)),
  tags: yup.array().of(yup.mixed().transform((item) => item && item.label)),
  serial: yup.string().matches(/^[a-z0-9-_]+$/i, {
    message: i18n.t('Provide a valid serial number'),
  }),
  tag: yup.string(),
  entireOrder: yup.boolean(),
});
